import type { Item } from "../api";
import { formatDate, toDateInputValue } from "./dates";

type ItemPickerField = {
    id: string;
    prefixless: boolean;
    value: (item: Item) => string;
    format: (item: Item) => string;
};

const FIELD_LABELS: Record<string, string> = {
    expiry: "Expiry",
    added: "Added",
    location: "Zone",
    quantity: "Quantity",
    brand: "Brand",
    category: "Category",
    notes: "Notes",
};

const PICKER_FIELDS: ItemPickerField[] = [
    {
        id: "expiry",
        prefixless: false,
        value: (item) => item.expiry_date ?? "",
        format: (item) => (item.expiry_date ? formatDate(item.expiry_date) : "No expiry"),
    },
    {
        id: "added",
        prefixless: false,
        value: (item) => toDateInputValue(item.created_at),
        format: (item) =>
            item.created_at ? formatDate(toDateInputValue(item.created_at)) : "Unknown date",
    },
    {
        id: "location",
        prefixless: false,
        value: (item) => String(item.location_id),
        format: (item) => item.location_name ?? "Unknown zone",
    },
    {
        id: "quantity",
        prefixless: false,
        value: (item) => String(item.quantity),
        format: (item) => String(item.quantity),
    },
    {
        id: "brand",
        prefixless: false,
        value: (item) => item.brand ?? "",
        format: (item) => item.brand ?? "",
    },
    {
        id: "category",
        prefixless: false,
        value: (item) => item.category ?? "",
        format: (item) => item.category ?? "",
    },
    {
        id: "name",
        prefixless: true,
        value: (item) => item.name,
        format: (item) => item.name,
    },
    {
        id: "photo",
        prefixless: true,
        value: (item) => item.image_path ?? "",
        format: (item) => formatPhotoValue(item),
    },
    {
        id: "notes",
        prefixless: false,
        value: (item) => item.notes?.trim() ?? "",
        format: (item) => item.notes?.trim() || "No notes",
    },
];

function formatPhotoValue(item: Item): string {
    if (!item.image_path) return "No photo";
    const filename = item.image_path.split("/").pop();
    return filename || "Photo";
}

function varyingFields(items: Item[]): ItemPickerField[] {
    return PICKER_FIELDS.filter((field) => {
        const values = new Set(items.map((item) => field.value(item)));
        return values.size > 1;
    });
}

function formatFieldPart(field: ItemPickerField, item: Item): string {
    const value = field.format(item).trim();
    if (!value) return "";

    if (field.prefixless) return value;

    const lead = FIELD_LABELS[field.id];
    return lead ? `${lead}: ${value}` : value;
}

export function formatItemPickerLabel(item: Item, items: Item[]): string {
    const fields = varyingFields(items);
    if (fields.length === 0) {
        return `Item #${item.id}`;
    }

    const parts = fields.map((field) => formatFieldPart(field, item)).filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : `Item #${item.id}`;
}
